import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Radio from '../../common/Radio';
import RadioGroup from '../../common/RadioGroup';
import Revenue from '../../common/Revenue';
import SlideCard from '../../common/SlideCard';
import {
  COMMISSION_TYPE_CUSTOM,
  COMMISSION_TYPE_MSRP,
  COMMISSION_TYPE_REV_SHARE,
  COMMISSION_TYPE_UNIT_PRICE,
  COMMISSION_TYPE_WHOLESALE,
  multiCurrencyCommissionFields,
} from '../../../constants/commissionTypes';
import styles from './RetailerCommission.module.scss';

const RETAIL_PERCENTAGE_REGEX = /^retail_price\*(\d+)\/100$/;
const BILLED_PERCENTAGE_REGEX = /^billed_amount\*(\d+)\/100$/;
const WHOLESALE_PRICE_REGEX = /^wholesale_price(?:\+(\d+))?$/;

function calculateWholesaleCommission(formula) {
  const commission = {};
  commission.type = COMMISSION_TYPE_WHOLESALE;
  const wholesaleParts = formula.match(WHOLESALE_PRICE_REGEX);
  if (wholesaleParts) {
    commission.wholesaleValue = wholesaleParts[1] ? parseInt(wholesaleParts[1], 10) / 100 : 0;
  }
  commission.isCustom = !wholesaleParts;
  return commission;
}

function calculateCommission(formula, commissionType) {
  const commission = {};
  commission.type = commissionType;
  let retailParts;
  switch (commissionType) {
    case COMMISSION_TYPE_MSRP:
      retailParts = formula.match(RETAIL_PERCENTAGE_REGEX);
      break;
    case COMMISSION_TYPE_REV_SHARE:
      retailParts = formula.match(BILLED_PERCENTAGE_REGEX);
      break;
    default:
      break;
  }

  if (retailParts) {
    commission.percentValue = 100 - retailParts[1];
  }
  commission.isCustom = !retailParts;
  return commission;
}

function calculateUnitPriceCommission(formula) {
  if (formula === null) {
    return { type: COMMISSION_TYPE_CUSTOM };
  } else if (formula.includes('unit_price')) {
    return { isCustom: false, type: COMMISSION_TYPE_UNIT_PRICE };
  } else if (formula.includes('wholesale_price')) {
    return calculateWholesaleCommission(formula);
  } else if (formula.includes('retail_price')) {
    return calculateCommission(formula, COMMISSION_TYPE_MSRP);
  } else if (formula.includes('billed_amount')) {
    return calculateCommission(formula, COMMISSION_TYPE_REV_SHARE);
  } else {
    return { isCustom: true };
  }
}

export function RetailerCommission(props) {
  const { formula, onChange } = props;

  const [changed, setChanged] = useState(false);
  const [commissionType, setCommissionType] = useState(COMMISSION_TYPE_CUSTOM);
  const [retailValue, setRetailValue] = useState(0);
  const [billedAmount, setBilledAmount] = useState(0);
  const [wholesaleValue, setWholesaleValue] = useState(0);

  const isCommissionPercentBased = useMemo(() => {
    return !!(commissionType === COMMISSION_TYPE_MSRP || commissionType === COMMISSION_TYPE_REV_SHARE);
  }, [commissionType]);

  const revenueKeyVal = useMemo(() => {
    return commissionType === COMMISSION_TYPE_MSRP
      ? { stateSetter: setRetailValue, value: retailValue }
      : { stateSetter: setBilledAmount, value: billedAmount };
  }, [commissionType, retailValue, billedAmount]);

  const commission = useMemo(() => {
    const commission = calculateUnitPriceCommission(formula);
    setCommissionType(commission.type);
    if (commission.percentValue) {
      if (commission.type === COMMISSION_TYPE_MSRP) setRetailValue(commission.percentValue);
      else setBilledAmount(commission.percentValue);
    }

    if (commission.wholesaleValue) {
      setWholesaleValue(commission.wholesaleValue);
    }

    return commission;
  }, [formula]);

  const commissionDescription = useMemo(() => {
    if (isCommissionPercentBased && formula) {
      return revenueKeyVal.value ? `${revenueKeyVal.value}%` : 'No Commission';
    }
    if (commissionType === COMMISSION_TYPE_UNIT_PRICE || commissionType === COMMISSION_TYPE_WHOLESALE) {
      return null;
    }
    return 'Custom Unit prices';
  }, [commissionType, formula, isCommissionPercentBased, revenueKeyVal.value]);

  const renderRetailerCommissionRadioOptions = useCallback(() => {
    return multiCurrencyCommissionFields.map((field) => (
      <Radio id={field.type} key={field.type} value={field.type} label={field.label} />
    ));
  }, []);

  useEffect(() => {
    if (!changed) {
      return;
    }
    switch (commissionType) {
      case COMMISSION_TYPE_WHOLESALE:
        onChange(COMMISSION_TYPE_WHOLESALE, wholesaleValue);
        break;
      case COMMISSION_TYPE_UNIT_PRICE:
        onChange(COMMISSION_TYPE_UNIT_PRICE, null);
        break;
      case COMMISSION_TYPE_MSRP:
        onChange(COMMISSION_TYPE_MSRP, retailValue);
        break;
      case COMMISSION_TYPE_REV_SHARE:
        onChange(COMMISSION_TYPE_REV_SHARE, billedAmount);
        break;
      case COMMISSION_TYPE_CUSTOM:
        onChange(null);
        break;
      default:
        break;
    }
  }, [onChange, changed, commissionType, retailValue, billedAmount, wholesaleValue]);

  if (commission.isCustom) {
    return <SlideCard title="Sales Channel's commission" description="Customized Calculation" />;
  }

  return (
    <SlideCard title={"Sales Channel's commission"} description={commissionDescription} testId="retailer-commission">
      <RadioGroup
        name="retailerCommissionType"
        onChange={(value) => {
          setCommissionType(value);
          setChanged(true);
        }}
        selectedvalue={commissionType}
      >
        {renderRetailerCommissionRadioOptions()}
      </RadioGroup>
      <div className={styles.retailerCommission}>
        {isCommissionPercentBased && (
          <Revenue
            index={1}
            onChange={(value) => {
              revenueKeyVal.stateSetter(value);
              setChanged(true);
            }}
            value={revenueKeyVal.value}
          />
        )}
      </div>
    </SlideCard>
  );
}

RetailerCommission.propTypes = {
  formula: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
